import React, { useEffect, useState } from 'react';
import { ChecklistExample } from '../../components/ChecklistExample';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { ContactModal } from '../../components/ContactModal';
import { API } from '../../utils/api';
import { trackPageView, trackCTAClick } from '../../utils/analytics';
import { 
  ArrowRight, 
  Check,
  Shield,
  Lock,
  CreditCard
} from '@phosphor-icons/react';

const fadeIn = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 }
};

const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};

export default function HomePage() {
  const { language } = useLanguage();

  useEffect(() => {
    trackPageView('homepage');
  }, []);

  const handleCTAClick = (ctaName) => {
    trackCTAClick(ctaName, 'homepage');
  };

  // Content
  const content = {
    en: {
      hero: {
        headline: "Your first 30 days in Canada, organized.",
        subheadline: "Get a checklist for your situation and province. Preview your plan for free, then choose whether to unlock detailed guidance.",
        cta: "Build my free preview",
        ctaSecondary: "Explore an example",
        trust: "Free account. No card required. Full access: C$49, paid once."
      },
      problem: {
        title: "The first weeks in Canada are overwhelming",
        items: [
          "Doing things in the wrong order",
          "Missing important documents",
          "Wasting time on unclear steps",
          "Not knowing what comes next"
        ]
      },
      solution: {
        title: "How it works",
        subtitle: "Three simple steps to get organized",
        steps: [
          { number: "1", title: "Describe your profile", desc: "Answer a few questions to personalize your plan" },
          { number: "2", title: "Get your checklist", desc: "Receive a personalized list of everything you need to do" },
          { number: "3", title: "Follow each step", desc: "Complete tasks in order with clear guidance" }
        ]
      },
      productProof: {
        title: "This is exactly what you get",
        subtitle: "A clear, organized checklist built for your situation"
      },
      trust: {
        title: "Built for newcomers",
        items: [
          "Covers essential first steps",
          "Province-specific guidance",
          "Helps avoid common mistakes"
        ]
      },
      whyPaperpal: {
        title: "Why use PaperPal",
        items: [
          { title: "Keeps everything organized", desc: "All your tasks in one place" },
          { title: "Shows the right order", desc: "Know exactly what to do next" },
          { title: "Saves time", desc: "Find guidance alongside your tasks" },
          { title: "Reduces confusion", desc: "Clear explanations for each step" }
        ]
      },
      microTrust: [
        { icon: Check, text: "Step-by-step guidance" },
        { icon: Lock, text: "Private & secure" },
        { icon: CreditCard, text: "One-time payment" },
        { icon: Shield, text: "No subscriptions" }
      ],
      pricing: {
        title: "Choose your plan",
        subtitle: "Start free, upgrade when you're ready",
        free: {
          name: "Free",
          price: "$0",
          features: [
            "Basic checklist",
            "General guidance",
            "Limited steps"
          ],
          cta: "Start free"
        },
        premium: {
          name: "Premium",
          price: "$49",
          priceNote: "one-time",
          features: [
            "Full personalized checklist",
            "Detailed step-by-step guidance",
            "Document explanations",
            "Mistake prevention tips",
            "Province-specific content"
          ],
          cta: "Get full access"
        }
      },
      finalCta: {
        title: "Don't get stuck figuring this out",
        subtitle: "Create your free account and explore your plan. Upgrade only when you’re ready.",
        cta: "Build my free preview"
      }
    },
    fr: {
      hero: {
        headline: "Vos 30 premiers jours au Canada, étape par étape.",
        subheadline: "Obtenez une liste adaptée à votre situation et à votre province. Découvrez votre plan gratuitement, puis choisissez de débloquer les conseils détaillés.",
        cta: "Créer mon aperçu gratuit",
        ctaSecondary: "Explorer un exemple",
        trust: "Compte gratuit. Aucune carte requise. Accès complet : 49 $ CA, en un seul paiement."
      },
      problem: {
        title: "Les premières semaines au Canada sont difficiles",
        items: [
          "Faire les choses dans le mauvais ordre",
          "Oublier des documents importants",
          "Perdre du temps sur des étapes floues",
          "Ne pas savoir quoi faire ensuite"
        ]
      },
      solution: {
        title: "Comment ça marche",
        subtitle: "Trois étapes simples pour vous organiser",
        steps: [
          { number: "1", title: "Décrivez votre profil", desc: "Répondez à quelques questions pour personnaliser votre plan" },
          { number: "2", title: "Recevez votre liste", desc: "Obtenez une liste personnalisée de tout ce que vous devez faire" },
          { number: "3", title: "Suivez chaque étape", desc: "Complétez les tâches dans l'ordre avec des conseils clairs" }
        ]
      },
      productProof: {
        title: "Voici exactement ce que vous obtenez",
        subtitle: "Une liste claire et organisée adaptée à votre situation"
      },
      trust: {
        title: "Conçu pour les nouveaux arrivants",
        items: [
          "Couvre les premières étapes essentielles",
          "Conseils spécifiques à votre province",
          "Aide à éviter les erreurs courantes"
        ]
      },
      whyPaperpal: {
        title: "Pourquoi utiliser PaperPal",
        items: [
          { title: "Garde tout organisé", desc: "Toutes vos tâches au même endroit" },
          { title: "Montre le bon ordre", desc: "Sachez exactement quoi faire ensuite" },
          { title: "Fait gagner du temps", desc: "Retrouvez les conseils avec vos tâches" },
          { title: "Réduit la confusion", desc: "Explications claires pour chaque étape" }
        ]
      },
      microTrust: [
        { icon: Check, text: "Conseils étape par étape" },
        { icon: Lock, text: "Privé et sécurisé" },
        { icon: CreditCard, text: "Paiement unique" },
        { icon: Shield, text: "Sans abonnement" }
      ],
      pricing: {
        title: "Choisissez votre plan",
        subtitle: "Commencez gratuitement, passez au premium quand vous êtes prêt",
        free: {
          name: "Gratuit",
          price: "0$",
          features: [
            "Liste de base",
            "Conseils généraux",
            "Étapes limitées"
          ],
          cta: "Commencer gratuitement"
        },
        premium: {
          name: "Premium",
          price: "49$",
          priceNote: "paiement unique",
          features: [
            "Liste personnalisée complète",
            "Conseils détaillés étape par étape",
            "Explications des documents",
            "Conseils pour éviter les erreurs",
            "Contenu spécifique à la province"
          ],
          cta: "Obtenir l'accès complet"
        }
      },
      finalCta: {
        title: "Ne restez pas bloqué à tout comprendre seul",
        subtitle: "Créez votre compte gratuit et découvrez votre plan. Passez à l’accès complet quand vous le souhaitez.",
        cta: "Créer mon aperçu gratuit"
      }
    }
  };

  const t = content[language] || content.en;

  // Contact modal state
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  return (
    <div className="pp-home min-h-screen bg-[#F9FAFB]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Header onContactClick={() => setIsContactModalOpen(true)} />
      
      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
        language={language}
      />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section id="start" className="py-12 md:py-20" data-testid="hero-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
              <motion.div 
                initial="hidden"
                animate="visible"
                variants={fadeIn}
                transition={{ duration: 0.5 }}
              >
                <h1 
                  className="text-4xl sm:text-5xl md:text-[56px] font-semibold tracking-tight leading-[1.1] text-[#0F172A] mb-6"
                  data-testid="hero-headline"
                >
                  {t.hero.headline}
                </h1>
                <p 
                  className="text-lg md:text-xl text-[#475569] leading-relaxed mb-7 max-w-xl"
                  data-testid="hero-subheadline"
                >
                  {t.hero.subheadline}
                </p>
                
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mb-4">
                  <Link 
                    to="/signup" 
                    className="bg-[#0F172A] hover:bg-[#1E293B] text-white font-medium rounded-[12px] px-8 py-4 transition-colors duration-200 inline-flex justify-center items-center gap-2"
                    data-testid="hero-primary-cta"
                    onClick={() => handleCTAClick('hero_cta')}
                  >
                    {t.hero.cta}
                    <ArrowRight size={20} weight="bold" />
                  </Link>
                  <a 
                    href="#checklist-example"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('checklist-example')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="bg-white border border-[#E5E7EB] hover:bg-gray-50 text-[#0F172A] font-medium rounded-[12px] px-8 py-4 transition-colors duration-200 inline-flex justify-center items-center gap-2 cursor-pointer"
                    data-testid="hero-secondary-cta"
                  >
                    {t.hero.ctaSecondary}
                  </a>
                </div>
                
                <p className="text-sm text-[#475569]" data-testid="hero-trust-line">
                  {t.hero.trust}
                </p>
              </motion.div>
              
              <div id="checklist-example" className="scroll-mt-28 min-w-0">
                <ChecklistExample language={language} />
              </div>
            </div>
          </div>
        </section>

        {/* Solution / How It Works */}
        <section id="how-it-works" className="py-14 md:py-20" data-testid="solution-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A] mb-4">
                {t.solution.title}
              </h2>
              <p className="text-lg text-[#475569]">{t.solution.subtitle}</p>
            </div>
            
            <motion.div 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={stagger}
              className="grid md:grid-cols-3 gap-8 lg:gap-12 max-w-5xl mx-auto"
            >
              {t.solution.steps.map((step, index) => (
                <motion.div
                  key={index}
                  variants={fadeIn}
                  className="text-left rounded-2xl border border-[#E5E7EB] bg-white p-6"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#E6F1EE] flex items-center justify-center mb-5">
                    <span className="text-lg font-semibold text-[#236C64]">{step.number}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-[#0F172A] mb-3">{step.title}</h3>
                  <p className="text-[#475569] leading-relaxed">{step.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* One concise benefits section replaces repeated problem/trust blocks. */}
        <section className="py-14 md:py-20 bg-white border-y border-[#E5E7EB]" data-testid="why-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="mb-8 max-w-2xl">
              <p className="text-sm font-semibold text-[#236C64] mb-3">{t.trust.title}</p>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A]">{t.whyPaperpal.title}</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {t.whyPaperpal.items.map((item) => (
                <div key={item.title} className="border-l-2 border-[#B8D7D1] pl-5 py-1">
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-2">{item.title}</h3>
                  <p className="text-[#475569] leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Micro Trust Indicators */}
        <section className="py-6 bg-[#F9FAFB] border-b border-[#E5E7EB]" data-testid="micro-trust-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
              {t.microTrust.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="flex items-center gap-2 text-[#475569] text-sm">
                    <Icon size={18} weight="bold" className="text-[#0F172A]" />
                    <span>{item.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="py-14 md:py-20 bg-[#F9FAFB]" data-testid="pricing-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A] mb-4">
                {language === 'fr' ? 'Choisissez votre plan' : 'Choose your plan'}
              </h2>
              <p className="text-lg text-[#64748B]">
                {language === 'fr' ? 'Commencez gratuitement ou débloquez tout.' : 'Start free or unlock everything.'}
              </p>
            </div>
            
            {/* Two-Card Grid */}
            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-6 md:gap-8 items-start">
              
              {/* Free Preview Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="relative"
              >
                <div className="h-[30px] mb-3" /> {/* Spacer to align with Premium badge */}
                
                <div className="bg-white border border-[#0F172A] rounded-[12px] shadow-[0_4px_24px_rgba(0,0,0,0.05)] p-7 md:p-8 h-full">
                  
                  {/* Title */}
                  <h3 className="text-xl font-medium text-[#0F172A] mb-5">
                    {language === 'fr' ? 'Aperçu gratuit' : 'Free preview'}
                  </h3>
                  
                  {/* Price */}
                  <div className="mb-6">
                    <span className="text-5xl font-semibold text-[#0F172A]" data-testid="pricing-free-price">{language === 'fr' ? '0 $' : '$0'}</span>
                  </div>
                  
                  {/* Benefits List */}
                  <ul className="space-y-3.5 mb-6">
                    {[
                      language === 'fr' ? 'Liste adaptée à votre profil et à votre province' : 'Checklist tailored to your profile and province',
                      language === 'fr' ? 'Présentation des étapes de votre liste' : 'Overview of the steps in your checklist',
                      language === 'fr' ? 'Compte gratuit, sans carte bancaire' : 'Free account, no payment card required'
                    ].map((benefit, index) => (
                      <li key={index} className="flex items-start gap-3" data-testid={`pricing-free-benefit-${index + 1}`}>
                        <Check size={18} weight="bold" className="text-[#0F172A] flex-shrink-0 mt-0.5" />
                        <span className="text-[#475569] text-[15px] leading-relaxed">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* CTA Button */}
                  <Link 
                    to="/signup"
                    className="w-full bg-white hover:bg-[#F8FAFC] text-[#0F172A] font-medium rounded-[10px] px-6 py-3.5 transition-colors duration-200 inline-flex justify-center items-center border border-[#0F172A]"
                    data-testid="pricing-free-cta"
                    onClick={() => handleCTAClick('pricing_free_cta')}
                  >
                    {language === 'fr' ? 'Commencer gratuitement' : 'Start for free'}
                  </Link>
                </div>
              </motion.div>
              
              {/* Premium Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="relative"
              >
                {/* Recommandé Badge */}
                <div className="flex justify-center mb-3">
                  <span className="bg-[#0F172A] text-white text-xs font-medium px-4 py-1.5 rounded-full">
                    {language === 'fr' ? 'Recommandé' : 'Recommended'}
                  </span>
                </div>
                
                {/* Card */}
                <div className="bg-white border-2 border-[#0F172A] rounded-[12px] shadow-[0_4px_24px_rgba(0,0,0,0.08)] p-7 md:p-8">
                  
                  {/* Title */}
                  <h3 className="text-xl font-medium text-[#0F172A] mb-5">
                    {language === 'fr' ? 'Accès complet' : 'Full Access'}
                  </h3>
                  
                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-3">
                      <span className="text-5xl font-semibold text-[#0F172A]" data-testid="pricing-premium-price">{language === 'fr' ? '49 $ CA' : 'C$49'}</span>
                    </div>
                    <p className="text-sm text-[#64748B] mt-1" data-testid="pricing-premium-payment-frequency">
                      {language === 'fr' ? 'paiement unique' : 'one-time payment'}
                    </p>
                  </div>
                  
                  {/* Benefits List */}
                  <ul className="space-y-3.5 mb-6">
                    {[
                      language === 'fr' ? 'Conseils détaillés pour les étapes de votre plan' : 'Detailed guidance for the steps in your plan',
                      language === 'fr' ? 'Suivi de progression et notes personnelles' : 'Progress tracking and personal notes',
                      language === 'fr' ? 'Conseils sur les documents à préparer' : 'Guidance on documents to prepare',
                      language === 'fr' ? 'Erreurs courantes à éviter' : 'Common mistakes to look out for',
                      language === 'fr' ? 'Paiement unique, sans abonnement' : 'One payment, no subscription'
                    ].map((benefit, index) => (
                      <li key={index} className="flex items-start gap-3" data-testid={`pricing-premium-benefit-${index + 1}`}>
                        <Check size={18} weight="bold" className="text-[#0F172A] flex-shrink-0 mt-0.5" />
                        <span className="text-[#475569] text-[15px] leading-relaxed">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* CTA Button */}
                  <Link 
                    to="/signup"
                    className="w-full bg-[#0F172A] hover:bg-[#1E293B] text-white font-medium rounded-[10px] px-6 py-3.5 transition-colors duration-200 inline-flex justify-center items-center"
                    data-testid="pricing-premium-cta"
                    onClick={() => handleCTAClick('pricing_premium_cta')}
                  >
                    {language === 'fr' ? 'Débloquer mon plan complet' : 'Unlock my full plan'}
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQ Preview */}
        <FAQPreview language={language} />

        {/* Final CTA */}
        <section className="py-14 md:py-20 bg-[#0F172A]" data-testid="final-cta-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
            <h2 className="text-3xl sm:text-4xl font-semibold text-white mb-4">
              {t.finalCta.title}
            </h2>
            <p className="text-white/70 mb-10 text-lg">{t.finalCta.subtitle}</p>
            <Link 
              to="/signup" 
              className="inline-flex items-center gap-2 px-10 py-4 bg-white text-[#0F172A] font-semibold rounded-[12px] transition-all hover:bg-gray-50"
              data-testid="final-cta-button"
              onClick={() => handleCTAClick('final_cta')}
            >
              {t.finalCta.cta}
              <ArrowRight size={20} weight="bold" />
            </Link>
          </div>
        </section>
      </main>

      <Footer onContactClick={() => setIsContactModalOpen(true)} />
    </div>
  );
}

function FAQPreview({ language }) {
  const [faqs, setFaqs] = React.useState([]);

  React.useEffect(() => {
    fetch(`${API}/faq`)
      .then(res => res.json())
      .then(data => setFaqs(data.slice(0, 3)))
      .catch(console.error);
  }, []);

  if (faqs.length === 0) return null;

  const title = language === 'fr' ? 'Questions fréquentes' : 'Common questions';
  const viewAll = language === 'fr' ? 'Voir toutes les questions' : 'View all questions';

  return (
    <section id="faq" className="py-14 md:py-20" data-testid="faq-preview-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A]">
            {title}
          </h2>
        </div>
        
        <div className="max-w-2xl mx-auto space-y-4">
          {faqs.map((faq) => (
            <div key={faq.id} className="bg-white border border-[#E5E7EB] rounded-[12px] p-6">
              <h3 className="font-semibold text-[#0F172A] mb-2">
                {language === 'fr' ? faq.question_fr : faq.question_en}
              </h3>
              <p className="text-[#475569] leading-relaxed">
                {language === 'fr' ? faq.answer_fr : faq.answer_en}
              </p>
            </div>
          ))}
        </div>
        
        <div className="text-center mt-10">
          <Link 
            to="/faq" 
            data-testid="homepage-faq-link"
            className="bg-white border border-[#E5E7EB] hover:bg-gray-50 text-[#0F172A] font-medium rounded-[12px] px-6 py-3 transition-colors duration-200 inline-flex items-center gap-2"
          >
            {viewAll}
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
}
